import { Component, OnDestroy, OnInit } from '@angular/core';
import { DateFilterFn } from '@angular/material/datepicker';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs';

import * as conditionGroups from '@shared-constants/condition-groups';
import { arrayIsTruthy } from '@shared-helpers/array-object-helpers';
import { IWebServerResponseApproverList } from '@shared-interfaces/api';
import { IExtDocument } from '@shared-interfaces/documents';

import { CostCenterHandler } from '../../scripts/costCenter';
import { ApiService } from '../../services/api.service';
import { DatahubService } from '../../services/datahub.service';
import { TimeoutService } from '../../services/timeout.service';
import { IContract, IGeneralForeman } from './contract.model';

@Component({
  selector: 'contract',
  templateUrl: './contract.component.html',
  styleUrl: './contract.component.css',
  standalone: false,
})
export class ContractComponent implements OnInit, OnDestroy {
  public isSearching = true;
  public timesheetOwners: IGeneralForeman[] = [];
  public visibleTimesheets: IExtDocument[] = [];
  public contracts: IContract[] = [];
  public customers: IContract[] = [];
  public uniqueContracts: IContract[] = [];
  public shouldShowTimesheetSelect = false;
  public selectedOwner?: IGeneralForeman;
  public owners: IWebServerResponseApproverList[] = [];
  public timesheets: IExtDocumentForGenericFieldsUi[] = [];
  public isOwnerSelected = false;
  public isCustGroupSelected = false;
  public timesheetHeaderDisplayText = '';

  public readonly thisWeek: DateFilterFn<Moment | null> = (d: Moment | null): boolean => {
    //Note: This looks a bit weird but it's a property that returns a method which is used in the HTML template which is how the MAT datepicker needs it
    const date = moment(d);
    return this.datahub.date.isSameWeek(date);
  };

  private readonly customerGroupDisplayNames: Record<string, string> = {
    GEN: 'T&M',
    MIL: 'Line Mile',
    UNT: 'Unit',
    CRW: 'Crew',
    LSM: 'Lump Sum',
    STM: 'STORM',
  };

  private readonly conditionGroupDisplayNames: Record<string, string> = {
    ST: 'Off System Storm',
    '02': 'Private Work',
    WS: 'Wolf Off Sys Storm',
  };

  private stormPrivateContracts: IContract[] = [];
  private standardContracts: IContract[] = [];
  private prevOwnerTab?: IGeneralForeman;
  private prevCustTab?: IContract;
  private selectedCostCenter!: CostCenterHandler;
  private dateSubscription = new Subscription();
  private ccHandler = new Subscription();
  private readonly costCenter: CostCenterHandler = this.datahub.costCenter;

  constructor(
    public readonly datahub: DatahubService, //this injection is used in the HTML template!
    private readonly apiService: ApiService,
    private readonly timeout: TimeoutService,
  ) {}

  public ngOnInit(): void {
    this.timeout.cancelTimeout();
    this.datahub.timesheet.reset();
    this.datahub.costCenter.shouldMissingCostCenterThrowError = true;
    this.selectedCostCenter = this.datahub.costCenter;
    this.handleDateChange();
    this.handleCostCenterChange();
  }

  public ngOnDestroy(): void {
    this.dateSubscription.unsubscribe();
    this.ccHandler.unsubscribe();
  }

  public selectCustomer(selectedContract: IContract): void {
    if (this.selectedOwner) {
      this.shouldShowTimesheetSelect = true;
      this.searchTimesheets(selectedContract, this.selectedOwner.number);
    }
  }

  public selectTimesheet(timesheet: IExtDocument): void {
    this.datahub.navigate(`/timesheet?number=${timesheet.Documentno}&date=${this.datahub.date.getSapDateString()}`);
  }

  public showCustomers(selectedContract: IContract): void {
    if (this.prevCustTab) {
      this.isCustGroupSelected = true;
      this.prevCustTab.isSelected = false;
      selectedContract.isSelected = !selectedContract.isSelected;
    } else {
      this.isCustGroupSelected = true;
      selectedContract.isSelected = !selectedContract.isSelected;
    }

    if (
      selectedContract.CondGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_STORM ||
      selectedContract.CondGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_PRIVATE ||
      selectedContract.CondGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_WOLF_STORM
    ) {
      this.customers = this.contracts.filter((contract) => contract.CondGroup === selectedContract.CondGroup);
    } else {
      this.customers = this.contracts.filter(
        (contract) =>
          contract.CustGroup === selectedContract.CustGroup &&
          contract.CondGroup !== conditionGroups.CONDITION_GROUP_SPECIAL_ID_STORM &&
          contract.CondGroup !== conditionGroups.CONDITION_GROUP_SPECIAL_ID_PRIVATE &&
          contract.CondGroup !== conditionGroups.CONDITION_GROUP_SPECIAL_ID_WOLF_STORM,
      );
    }

    if (this.customers.length === 1 && this.customers[0] != null) this.selectCustomer(this.customers[0]);
    this.prevCustTab = selectedContract;
  }

  public showCustGroups(selectedOwner: IGeneralForeman): void {
    this.selectedOwner = selectedOwner;
    // if only one option to select, move through and show next level
    if (this.uniqueContracts.length === 1 && this.customers[0] != null && this.uniqueContracts[0] != null) {
      this.prevCustTab = this.uniqueContracts[0];
      this.uniqueContracts[0].isSelected = true;
      this.showCustomers(this.uniqueContracts[0]);
    } else {
      this.customers = [];
    }

    if (!this.customers.length && this.prevCustTab) this.prevCustTab.isSelected = false;
    if (!this.prevOwnerTab || this.prevOwnerTab === selectedOwner) {
      selectedOwner.isSelected = !selectedOwner.isSelected;
      this.isOwnerSelected = !this.isOwnerSelected;
      this.isCustGroupSelected = false;
    } else {
      this.isOwnerSelected = true;
      this.prevOwnerTab.isSelected = false;
      selectedOwner.isSelected = !selectedOwner.isSelected;
    }
    this.prevOwnerTab = selectedOwner;
  }

  private setCustomerGroupDisplayTabs(standardContracts: IContract[], specialContracts: IContract[]): void {
    // Go through contracts and if there are duplicates when looking at "CustGroup" then don't return it
    // Only exception is storm and private work which have custgroup of GEN but we want those to show
    this.uniqueContracts = standardContracts.filter((contract, index, self) => {
      return self.findIndex((c) => c.CustGroup === contract.CustGroup) === index;
    });
    this.uniqueContracts = this.uniqueContracts.concat(specialContracts);
  }

  public searchContracts(): void {
    this.isOwnerSelected = false;
    this.isCustGroupSelected = false;
    this.shouldShowTimesheetSelect = false;
    this.contracts = [];
    this.stormPrivateContracts = [];
    this.standardContracts = [];
    this.isSearching = true;

    this.apiService.getApprovers$().subscribe({
      next: (approversList) => {
        this.owners = approversList.filter((x: IWebServerResponseApproverList) =>
          x.costCenters.some((y) => this.costCenter.ctrl.value.trim() === y),
        ); //Get only approvers that have jurisdiction in the selected cost center.
        if (!arrayIsTruthy(this.owners)) {
          this.endSearch();
          return;
        }

        this.timesheetOwners = this.owners.map((x) => {
          return {
            number: x.value,
            name: x.display,
            initials: this.getFirstLetters(x.display),
            // extDocuments: documents,
            isSelected: false,
          };
        });

        this.apiService
          .getContractsByDateAndCC$(
            this.datahub.costCenter.ctrl.value,
            this.datahub.date.getFilterString(this.datahub.date.selectedDate.value),
          )
          .pipe(
            mergeMap((contracts: IContract[]) => contracts),
            map((contract: IContract) => {
              // if storm (ST), private work (02), or wolf storm (WS) then we want it to return at same level as CustGroup
              if (
                contract.CondGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_PRIVATE ||
                contract.CondGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_STORM ||
                contract.CondGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_WOLF_STORM
              ) {
                contract.displayName = this.conditionGroupDisplayNames[contract.CondGroup] ?? 'No Display Name';
                this.stormPrivateContracts.push(contract);
              } else {
                contract.displayName = this.customerGroupDisplayNames[contract.CustGroup] ?? 'No Display Name';
                this.standardContracts.push(contract);
              }

              contract.isSelected = false;
              return contract;
            }),
            toArray(),
          )
          .subscribe(() => {
            this.contracts = this.standardContracts.concat(this.stormPrivateContracts);
            this.setCustomerGroupDisplayTabs(this.standardContracts, this.stormPrivateContracts);
          });
        this.isSearching = false;
      },
    });
  }

  private searchTimesheets(contract: IContract, selectedOwnerNumber: string): void {
    this.isSearching = true;
    this.timesheets = [];
    const timesheetParams = {
      Owner: [selectedOwnerNumber],
      Dateentered: this.datahub.date.getFilterString(this.datahub.date.getWeDate()),
      Salesdoc: contract.ServiceOrder,
    };
    this.apiService.getTimesheets$(timesheetParams).subscribe((timesheets: IExtDocument[]) => {
      this.timesheets = timesheets;

      if (this.timesheets.length > 1) {
        /**
         * Loops through `Genericfld01` to `Genericfld10` fields of each timesheet in the `timesheets` array,
         * and concatenates the non-empty field values into a single string separated by the `~` character.
         * The resulting string is stored in the `timesheetHeaderDisplayText` property.
         */
        this.timesheets.forEach((timesheet: IExtDocumentForGenericFieldsUi) => {
          let combinedValue = '';
          for (let i = 1; i <= 10; i++) {
            const fieldName = `Genericfld${i.toString().padStart(2, '0')}` as keyof IExtDocument;
            const fieldValue = timesheet[fieldName] as string;
            if (fieldValue) {
              combinedValue += (combinedValue ? ' ~ ' : '') + fieldValue;
            }
          }
          timesheet.timesheetHeaderDisplayText = combinedValue;
        });
      } else if (this.timesheets.length === 1 && this.timesheets[0] != null) {
        this.selectTimesheet(this.timesheets[0]);
      } else {
        this.endSearch();
        return;
      }

      this.timesheetOwners = this.timesheetOwners.filter((x) => arrayIsTruthy(x.extDocuments ?? []));
      this.isSearching = false;
    });
  }

  private endSearch(): void {
    this.isSearching = false;
    this.timesheetOwners = [];
  }

  /**
   * @description Gets the first letters from the first word and last word in a string
   * @example this.getFirstLetters('hello world') //"hw"
   * @example this.getFirstLetters('hello world i am bill') //"hb"
   */
  private getFirstLetters(str: string): string {
    if (!str) return '';
    const firstLetters = str.split(' ').map((word) => word[0]);
    return `${firstLetters[0] ?? ''}${firstLetters[firstLetters.length - 1] ?? ''}`;
  }

  private handleDateChange(): void {
    this.dateSubscription = this.datahub.date.getControl().valueChanges.subscribe(() => {
      this.searchContracts();
    });
  }

  private handleCostCenterChange(): void {
    this.ccHandler = this.selectedCostCenter.finishedLookupSubject$.subscribe((hasFinished) => {
      if (!hasFinished) return;
      this.searchContracts();
    });
  }
}

interface IExtDocumentForGenericFieldsUi extends IExtDocument {
  timesheetHeaderDisplayText?: string;
}
